/** Presentation helpers shared across the screens. */

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "4 min ago" reads faster than a timestamp when scanning for what just broke. */
export function relativeTime(value: string | null | undefined): string {
  if (!value) return 'never';
  const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const units: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.35, 'week'],
    [12, 'month'],
  ];
  let amount = seconds / 60;
  let unit: Intl.RelativeTimeFormatUnit = 'minute';
  for (const [step, nextUnit] of units) {
    if (Math.abs(amount) < step) break;
    amount /= step;
    unit = nextUnit;
  }
  return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(-Math.round(amount), unit);
}

/** DHIS2 monthly periods are yyyyMM; show them as "Jul 2026". */
export function formatPeriod(period: string): string {
  const match = period.match(/^(\d{4})(\d{2})$/);
  if (!match) return period;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

export function formatNumber(value: number): string {
  return value.toLocaleString();
}

export const STATUS_LABELS: Record<string, string> = {
  sent: 'Delivered',
  queued: 'Queued',
  partial: 'Partial',
  unmapped: 'Unmapped',
  failed: 'Failed',
  received: 'Pending',
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export const JOB_LABELS: Record<string, string> = {
  pending: 'Waiting',
  running: 'Processing',
  succeeded: 'Done',
  failed: 'Failed',
  dead: 'Given up',
  cancelled: 'Cancelled',
};

/** Maps a job status onto the pill classes the status colours already use. */
export const JOB_TONE: Record<string, string> = {
  pending: 'received',
  running: 'partial',
  succeeded: 'sent',
  failed: 'failed',
  dead: 'failed',
  cancelled: 'neutral',
};

export const AUDIT_CATEGORY_LABELS: Record<string, string> = {
  auth: 'Sign-in',
  user: 'Accounts',
  config: 'Configuration',
  data: 'Data',
  system: 'System',
};

/** How long the oldest waiting job has been waiting, for the queue header. */
export function waitingFor(since: string | null): string {
  if (!since) return '-';
  const seconds = Math.max(0, Math.round((Date.now() - new Date(since).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}
